import { FormikContext } from 'formik';
import _ from 'lodash';
import {
	CSSProperties,
	ComponentProps,
	Fragment,
	PropsWithChildren,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { Box, BoxProps, BoxType } from 'reflexbox';

const PortalContext = createContext({
	update: undefined as unknown as (id: string, node: ReactNode) => void,
	unmount: undefined as unknown as (id: string) => void,
});

export function Portal(props: PropsWithChildren) {
	const context = useContext(PortalContext);
	const id = useRef(Math.random().toString()).current;

	const formik = useContext(FormikContext);

	useEffect(() =>
		context.update(
			id,
			formik ? (
				<FormikContext.Provider value={formik}>{props.children}</FormikContext.Provider>
			) : (
				props.children
			),
		),
	);

	useEffect(() => () => context.unmount(id), []);

	return null;
}

function PortalShim() {
	const [elements, setElements] = useState({} as { [id: string]: ReactNode });

	const context = useContext(PortalContext);

	useLayoutEffect(() => {
		context.update = function update(id: string, node: ReactNode) {
			setElements((elements) => ({
				...elements,
				[id]: node,
			}));
		};

		context.unmount = function unmount(id: string) {
			setElements((elements) => _.omit(elements, id));
		};
	});

	const count = Object.keys(elements).length;

	useLayoutEffect(() => {
		document.body.style.overflow = count > 0 ? 'hidden' : '';

		return () => {
			document.body.style.overflow = '';
		};
	}, [count]);

	return (
		<>
			{Object.keys(elements).map((key) => (
				<Fragment key={key}>{elements[key]}</Fragment>
			))}
		</>
	);
}

Portal.Provider = function PortalProvider(
	props: PropsWithChildren<BoxProps & { style?: CSSProperties }>,
) {
	return (
		<PortalContext.Provider value={{ update: () => {}, unmount: () => {} }}>
			{props.children}
			<Box {...(props as ComponentProps<BoxType>)}>
				<PortalShim />
			</Box>
		</PortalContext.Provider>
	);
};
